import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DownloadsNavButton from '../../components/Navigation/DownloadsNavButton'
import Toast from '../../components/Toast'
import { useSelector } from 'react-redux'
import { useAutocomplete, useSelect } from '@chilecompra/react-kit'
import { Autocomplete, Button, Card, Select, Typography } from '@chilecompra/react-kit/components'
import { generateYears } from '../../modules/utils/years'
import { REACT_APP_API_BASE_URL, REACT_APP_PAC_URL } from '../../config/settings/environment'
import {
  Description,
  GridContainer,
  GridItem,
  Root,
  Subtitle,
  Title
} from '../DownloadLargePurchaseProcessesSection/DownloadLargePurchaseProcessesSection.styles'

/**
 * The DownloadPurchasePlanSection's container.
 */
const DownloadPurchasePlanSection = () => {
  const { endYear } = useSelector(state => state.topNavbar)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    overwriteCallback: () => endYear
  })
  const {
    option: institutionOption,
    value: institutionValue,
    onChange: handleChangeInstitution,
    onSelect: handleSelectInstitution
  } = useAutocomplete({})
  const [institutionOptions, setInstitutionOptions] = useState([])
  const [loadingInstitutions, setLoadingInstitutions] = useState(false)

  useEffect(() => {
    const fetchInstitutions = async () => {
      setLoadingInstitutions(true)
      try {
        const res = await fetch(`${REACT_APP_API_BASE_URL}/v1/kpi/instituciones`)
        if (!res.ok) {
          setInstitutionOptions([])
          setLoadingInstitutions(false)
          return
        }
        const json = await res.json()
        const payload = json.payload || []
        const options = payload.map(item => ({
          value: item.codigoEntidad || item.rut || item.id,
          name: item.razonSocial,
          raw: item
        }))
        setInstitutionOptions(options)
      } catch (e) {
        setInstitutionOptions([])
      } finally {
        setLoadingInstitutions(false)
      }
    }

    fetchInstitutions()
  }, [])
  const yearsOptions = useMemo(() => generateYears(endYear, 2025), [endYear])
  const [toast, setToast] = useState({
    visible: false,
    variant: 'success',
    title: 'Descarga exitosa',
    message: 'Tu archivo comenzará a descargarse en breve.',
    key: 0
  })

  const handleCloseToast = useCallback(() => {
    setToast(previous => ({ ...previous, visible: false }))
  }, [])

  const handleClickDownload = useCallback(async () => {
    const codigoEntidad =
      institutionOption?.raw?.codigoEntidad ||
      (institutionOptions.find(o => o.value === institutionValue) || {}).raw?.codigoEntidad ||
      institutionValue

    const currentYear = new Date().getFullYear()
    const periodo = yearValue || currentYear
    const hasInstitution = Boolean(codigoEntidad)
    const hasYear = Boolean(yearValue)

    if (hasInstitution && !hasYear) {
      setToast(previous => ({
        ...previous,
        visible: true,
        variant: 'error',
        title: 'Error',
        message: 'Selecciona un año antes de descargar.',
        key: previous.key + 1
      }))
      return
    }

    const downloadUrl = hasInstitution
      ? `${REACT_APP_PAC_URL}/${periodo}/pacorganismos_${periodo}_${encodeURIComponent(codigoEntidad)}.zip`
      : `${REACT_APP_PAC_URL}/${periodo}/pacorganismos_${periodo}.zip`

    try {
      const response = await fetch(`${REACT_APP_API_BASE_URL}/v1/descarga/validate-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: downloadUrl })
      })

      if (!response.ok) {
        setToast(previous => ({
          ...previous,
          visible: true,
          variant: 'error',
          title: 'Error en la descarga',
          message: 'No fue posible generar el archivo del Plan Anual de Compras. Inténtalo nuevamente.',
          key: previous.key + 1
        }))
        return
      }

      const json = await response.json()
      const payload = json.payload || {}
      const { fallo, tamanio, codigo } = payload

      if (fallo && codigo !== '403') {
        setToast(previous => ({
          ...previous,
          visible: true,
          variant: 'error',
          title: 'Error en la descarga',
          message: 'No fue posible generar el archivo del Plan Anual de Compras. Inténtalo nuevamente.',
          key: previous.key + 1
        }))
        return
      }

      if (tamanio == null || codigo === '403') {
        setToast(previous => ({
          ...previous,
          visible: true,
          variant: 'warning',
          title: 'Información no disponible',
          message: 'En este momento no es posible generar la descarga. Por favor, intente más tarde',
          key: previous.key + 1
        }))
        return
      }

      const tamanioBytes = Number(tamanio)
      if (Number.isNaN(tamanioBytes) || tamanioBytes < 300) {
        setToast(previous => ({
          ...previous,
          visible: true,
          variant: 'warning',
          title: 'Archivo sin datos',
          message: 'No se encontraron registros con los filtros aplicados. Se descargó un archivo sin información.',
          key: previous.key + 1
        }))
        return
      }

      setToast(previous => ({
        ...previous,
        visible: true,
        variant: 'success',
        title: 'Descarga completada',
        message: 'El archivo del Plan Anual de Compras se descargó correctamente.',
        key: previous.key + 1
      }))

      window.open(downloadUrl, '_blank')
    } catch (e) {
      setToast(previous => ({
        ...previous,
        visible: true,
        variant: 'error',
        title: 'Error en la descarga',
        message: 'No fue posible generar el archivo del Plan Anual de Compras. Inténtalo nuevamente.',
        key: previous.key + 1
      }))
    }
  }, [institutionOption, institutionOptions, institutionValue, yearValue])

  return (
    <Root>
      <Toast
        visible={toast.visible}
        variant={toast.variant}
        title={toast.title}
        message={toast.message}
        duration={4000}
        onClose={handleCloseToast}
        closeable
        toastKey={toast.key}
      />
      <GridContainer padding="0 0 26px">
        <DownloadsNavButton />
        <GridItem xs={10} sm={11}>
          <Title>Planes de Compra</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 30px">
        <GridItem xs={12}>
          <Description>
            En esta sección, podrás acceder a información detallada sobre los Planes de compra de las instituciones que
            transan en Mercado Público. Nuestro objetivo es proporcionar datos completos y transparentes que faciliten
            el análisis y la gestión eficiente de estos procesos.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 26px">
        <GridItem xs={12}>
          <Subtitle>¿Qué son los planes de compra?</Subtitle>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 30px">
        <GridItem xs={12}>
          <Description>
            El Plan Anual de Compras (PAC) es la planificación de una lista referencial de los bienes y servicios que
            los organismos públicos esperan contratar durante un año. De esta manera, las entidades pueden tener la
            visión completa de sus procesos de compra y así distribuirlas a lo largo de los meses con su respectivo
            presupuesto.
            <br />
            <br />
            Esta lista contiene el detalle y cantidad de productos y servicios, su valor estimado, la fecha aproximada
            en que se realizará la compra y la modalidad o mecanismo de compra que se espera utilizar (Convenio Marco,
            licitación pública, Compra Coordinada, Gran Compra, Compra Ágil, entre otros).
            <br />
            <br />
            La elaboración y publicación del Plan Anual de Compras es obligatorio para todos los organismos del Estado
            que se rigen por la Ley de Compras Públicas N 19.886 y les permite evaluar los mecanismos más eficientes y
            participativos de adquisición para optimizar el uso de los recursos fiscales.
            <br />
            <br />
            Los costos de una compra imprevista, por ejemplo, resultarán mayores al de una compra planificada, por lo
            que recomendamos a los encargados de las áreas de adquisiciones a realizar una correcta planificación con el
            fin de evitar posibles sobreprecios o acumulación de contrataciones en los últimos meses del año.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 26px">
        <GridItem xs={12}>
          <Subtitle>¿Qué encontrarás aquí?</Subtitle>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 30px">
        <GridItem xs={12}>
          <Description>
            Archivo descargable en formato CSV que contiene los planes de compra para la institución y año
            seleccionados.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 26px">
        <GridItem xs={12}>
          <Subtitle>¿Cómo utilizar esta sección?</Subtitle>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 30px">
        <GridItem xs={12}>
          <Description>
            Selecciona el año y la institución de interés y descarga el archivo con el Plan de Compra.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 26px">
        <GridItem xs={12}>
          <Card color="primary" margin="0px 0">
            <Typography fontSize="14px" variant="body2">
              <strong>Contenido del archivo a descargar</strong>
              <br />
              <br />
              Si no seleccionas ningún filtro, se descargará el archivo completo con todos los registros disponibles del
              Plan Anual de Compras (PAC). Si aplicas filtros, la descarga incluirá únicamente la información
              correspondiente a esos criterios.
            </Typography>
          </Card>
        </GridItem>
      </GridContainer>
      <GridContainer alignItems="center" padding="0" spacing={1}>
        <GridItem xs={12} md={3}>
          <Select label="AÑO" options={yearsOptions} value={yearValue} onChange={handleChangeYear} />
        </GridItem>
        <GridItem xs={12} md={5}>
          <Autocomplete
            label="INSTITUCIÓN"
            option={institutionOption}
            options={institutionOptions}
            placeholder="Ingresa el nombre del organismo, por ejemplo: “Municipalidad de Santiago”"
            value={institutionValue}
            waiting={loadingInstitutions}
            onChange={handleChangeInstitution}
            onSelect={handleSelectInstitution}
          />
        </GridItem>
        <GridItem xs={12} md={4}>
          <Button color="primary" onClick={handleClickDownload}>
            Descargar archivo
          </Button>
        </GridItem>
      </GridContainer>
    </Root>
  )
}

export default DownloadPurchasePlanSection
